import React from 'react';
import styled from 'styled-components';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import Hero from '../components/Hero';
import Section from '../components/Section';
import Button from '../components/Button';
import StepProcess from '../components/StepProcess';
import { FaComments, FaPencilRuler, FaCode, FaRocket, FaHeadset, FaClock, FaCalendarAlt, FaLaptopCode, FaCheckCircle, FaGlobe, FaCheck } from 'react-icons/fa';

// Cards de beneficios con estilo moderno
const BenefitsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin: 3rem 0;
`;

const BenefitCard = styled.div`
  background-color: var(--white);
  border-radius: var(--border-radius);
  padding: 1.5rem;
  border: 1px solid #eee;
  box-shadow: var(--box-shadow-sm);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--box-shadow);
  }
`;

const BenefitIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
  
  svg {
    font-size: 1.5rem;
    color: var(--white);
  }
`;

const BenefitTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const BenefitDescription = styled.p`
  color: var(--gray);
  font-size: 0.95rem;
  line-height: 1.6;
`;

// Sección CTA modernizada
const CTASection = styled.div`
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  padding: 4rem 2rem;
  border-radius: var(--border-radius-lg);
  text-align: center;
  color: var(--white);
  margin: 3rem 0;
  box-shadow: var(--box-shadow);
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
`;

const CTADescription = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.9;
`;

// Títulos de sección mejorados
const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  position: relative;
  padding-bottom: 1rem;
  
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 80px;
    height: 4px;
    background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: var(--gray);
  margin-bottom: 2rem;
  max-width: 800px;
`;

// Reemplazar el timeline actual con un nuevo diseño de pasos horizontales
const DeliveryTimelineContainer = styled.div`
  max-width: 1200px;
  margin: 3rem auto;
  position: relative;
  padding: 0 1rem;
`;

const TimelineTrack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  position: relative;
  
  &::before {
    content: none;
  }
  
  @media (min-width: 992px) {
    flex-direction: row;
    
    &::before {
      content: '';
      position: absolute;
      top: 40px;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, #007bff, #0056b3);
      z-index: 0;
    }
  }
`;

const TimelineStep = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
  z-index: 1;
  
  @media (min-width: 992px) {
    &:not(:last-child)::after {
      content: '';
      position: absolute;
      top: 40px;
      right: -1rem;
      width: 2rem;
      height: 4px;
      background: linear-gradient(90deg, #007bff, #0056b3);
      z-index: 0;
    }
  }
`;

const TimelineDayBadge = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: var(--white);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;
  z-index: 2;
  
  @media (max-width: 991px) {
    margin-left: 0;
  }
`;

const TimelineDayNumber = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.125rem;
`;

const TimelineDayText = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
`;

const TimelineStepCard = styled.div`
  background-color: var(--white);
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow-sm);
  padding: 1.5rem;
  height: calc(100% - 80px - 1.5rem);
  border: 1px solid #eee;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--box-shadow);
  }
  
  @media (max-width: 991px) {
    margin-left: 2.5rem;
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      top: -1.5rem;
      left: -1.25rem;
      width: 3px;
      height: calc(100% + 1.5rem);
      background: linear-gradient(180deg, #007bff, #0056b3);
      z-index: 0;
    }
  }
`;

const TimelineStepTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.75rem;
    color: var(--blue);
    font-size: 1.25rem;
  }
`;

const TimelineStepDescription = styled.p`
  color: var(--gray);
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
`;

// Estilos personalizados para el componente StepProcess
const CustomStepProcessContainer = styled.div`
  max-width: 1200px;
  margin: 4rem auto;
  padding: 0 1rem;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ProcessCard = styled.div`
  background-color: var(--white);
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow-sm);
  border: 1px solid #eee;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--box-shadow);
  }
  
  &:last-child {
    @media (min-width: 768px) {
      grid-column: 1 / -1;
      display: flex;
      flex-direction: row;
    }
  }
`;

const ProcessImageContainer = styled.div`
  position: relative;
  padding-top: 56.25%;  // Proporción 16:9
  background-color: #f8f9fa;
  overflow: hidden;
  
  ${ProcessCard}:last-child & {
    @media (min-width: 768px) {
      padding-top: 0;
      width: 50%;
      min-height: 300px;
    }
  }
`;

const ProcessImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  ${ProcessCard}:hover & {
    transform: scale(1.05);
  }
  
  ${ProcessCard}:last-child & {
    @media (min-width: 768px) {
      position: absolute;
    }
  }
`;

const ProcessNumber = styled.div`
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: var(--white);
  font-size: 1.5rem;
  font-weight: bold;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 1;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ProcessContent = styled.div`
  padding: 2rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ProcessTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.75rem;
    color: var(--blue);
  }
`;

const ProcessDescription = styled.p`
  color: var(--gray);
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
`;

const ProcessHeading = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
`;

const ProcessSectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const ProcessSectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: var(--gray);
`;

const ProcessCTA = styled.div`
  text-align: center;
  margin-top: 3rem;
`;

const ProcesoPage = () => {
  // Datos para el proceso principal
  const processSteps = [
    {
      number: 1,
      title: "Consultoría Inicial",
      description: "Comenzamos con una conversación para entender tu negocio, objetivos y necesidades específicas. Analizamos tu competencia y definimos el alcance del proyecto.",
      image: "/fotos generales para el proyecto/proceso-consulta.webp",
      icon: <FaComments />
    },
    {
      number: 2,
      title: "Diseño y Planificación",
      description: "Creamos un wireframe o esquema de la web para definir su estructura. Elegimos la paleta de colores, tipografías y elementos visuales que mejor representen tu marca.",
      image: "/fotos generales para el proyecto/proceso-diseno.webp",
      icon: <FaPencilRuler />
    },
    {
      number: 3,
      title: "Desarrollo",
      description: "Transformamos el diseño en código utilizando tecnologías modernas como React y TypeScript. Implementamos todas las funcionalidades requeridas y técnicas de SEO on-page.",
      image: "/fotos generales para el proyecto/proceso-desarrollo.webp",
      icon: <FaCode />
    },
    {
      number: 4,
      title: "Lanzamiento",
      description: "Realizamos pruebas exhaustivas para garantizar funcionamiento correcto en distintos dispositivos y navegadores. Tras tu aprobación, lanzamos la web configurando dominio y hosting.",
      image: "/fotos generales para el proyecto/proceso-lanzamiento.webp",
      icon: <FaRocket />
    },
    {
      number: 5,
      title: "Soporte y Mantenimiento",
      description: "Te ofrecemos soporte técnico para resolver cualquier duda o incidencia. El mantenimiento mensual de 20€ incluye hosting, dominio y soporte técnico para mantener tu web actualizada y funcionando correctamente.",
      image: "/fotos generales para el proyecto/proceso-soporte.webp",
      icon: <FaHeadset />
    }
  ];

  // Datos para la cronología
  const timelineSteps = [
    {
      number: 1,
      title: "Día 1: Inicio del proyecto",
      description: "Tras recibir el pago inicial, comenzamos con la consultoría y la recopilación de información sobre tu negocio y tus necesidades específicas.",
      icon: <FaCalendarAlt />
    },
    {
      number: 2,
      title: "Día 2-3: Diseño y estructura",
      description: "Definimos la estructura de la web y creamos los wireframes. Te presentamos una propuesta para su aprobación o modificación.",
      icon: <FaPencilRuler />
    },
    {
      number: 3,
      title: "Día 4-6: Desarrollo",
      description: "Implementamos el diseño aprobado, programamos todas las funcionalidades y aplicamos las optimizaciones SEO necesarias.",
      icon: <FaLaptopCode />
    },
    {
      number: 4,
      title: "Día 7: Revisión y ajustes",
      description: "Te mostramos la web terminada para tu revisión. Realizamos los ajustes necesarios basados en tus comentarios.",
      icon: <FaCheckCircle />
    },
    {
      number: 5,
      title: "Día 8: Lanzamiento",
      description: "Configuramos el hosting y el dominio, y lanzamos tu web al público. ¡Tu negocio ya está online y listo para crecer!",
      icon: <FaGlobe />
    }
  ];

  const beneficios = [
    {
      title: "Proceso ágil",
      description: "Trabajamos con metodologías ágiles para entregar tu web en tiempo récord sin sacrificar calidad.",
      icon: <FaClock />
    },
    {
      title: "Comunicación directa",
      description: "Tendrás contacto directo con nuestro equipo en todo momento para resolver dudas y hacer ajustes.",
      icon: <FaComments />
    },
    {
      title: "Tecnología avanzada",
      description: "Utilizamos React y otras tecnologías modernas que ofrecen mejor rendimiento que WordPress.",
      icon: <FaLaptopCode />
    },
    {
      title: "Trabajo finalizado",
      description: "Entregamos un proyecto 100% terminado, optimizado para SEO y listo para captar clientes.",
      icon: <FaCheckCircle />
    }
  ];

  return (
    <Layout>
      <SEO 
        title="Nuestro Proceso de Trabajo | Web a Bajo Coste"
        description="Conoce cómo trabajamos en Web a Bajo Coste: desde la consultoría inicial hasta el lanzamiento de tu web y el soporte posterior."
        keywords="proceso diseño web, metodología web, etapas diseño web"
        canonical="https://webabajocoste.es/proceso"
      />
      
      <Hero 
        title="Nuestro Proceso de Trabajo"
        subtitle="Cómo convertimos tu idea en una web efectiva, paso a paso"
        backgroundImage="/fotos generales para el proyecto/proceso-hero.webp"
        badges={['Entrega en 7 días', 'Proceso optimizado', 'Comunicación directa']}
      />
      
      <Section>
        <SectionTitle>¿Cómo lo hacemos?</SectionTitle>
        <SectionSubtitle>
          Un proceso simple pero efectivo para conseguir los mejores resultados
        </SectionSubtitle>
        
        <p>
          En Web a Bajo Coste hemos desarrollado un método de trabajo eficiente que nos permite entregar 
          páginas web de calidad en tiempo récord y a precios accesibles. Nuestra metodología se basa en la 
          transparencia, la comunicación directa y la eficiencia en cada etapa del proceso.
        </p>
        
        <BenefitsContainer>
          {beneficios.map((beneficio, index) => (
            <BenefitCard key={index}>
              <BenefitIcon>
                {beneficio.icon}
              </BenefitIcon>
              <BenefitTitle>{beneficio.title}</BenefitTitle>
              <BenefitDescription>{beneficio.description}</BenefitDescription>
            </BenefitCard>
          ))}
        </BenefitsContainer>
      </Section>
      
      {/* Fases de trabajo personalizado con layout de 2 columnas */}
      <Section background="light">
        <CustomStepProcessContainer>
          <ProcessHeading>
            <ProcessSectionTitle>Nuestras fases de trabajo</ProcessSectionTitle>
            <ProcessSectionSubtitle>
              Un proceso optimizado para entregarte tu web en tiempo récord
            </ProcessSectionSubtitle>
          </ProcessHeading>
          
          <ProcessGrid>
            {processSteps.map((step) => (
              <ProcessCard key={step.number}>
                <ProcessImageContainer>
                  <ProcessNumber>{step.number}</ProcessNumber>
                  {step.image && <ProcessImage src={step.image} alt={step.title} />}
                </ProcessImageContainer>
                <ProcessContent>
                  <ProcessTitle>
                    {step.icon} {step.title}
                  </ProcessTitle>
                  <ProcessDescription>{step.description}</ProcessDescription>
                </ProcessContent>
              </ProcessCard>
            ))}
          </ProcessGrid>
          
          <ProcessCTA>
            <Button as="a" href="/contacto" size="large">
              Solicitar presupuesto
            </Button>
          </ProcessCTA>
        </CustomStepProcessContainer>
      </Section>
      
      <Section
        background="light"
      >
        <SectionTitle>Plazos de entrega</SectionTitle>
        <SectionSubtitle>
          Nos comprometemos a cumplir con plazos de entrega ágiles sin comprometer la calidad
        </SectionSubtitle>
        
        <p>
          Entendemos que el tiempo es un factor crucial para tu negocio. Por eso, nos comprometemos a 
          cumplir con plazos de entrega ágiles sin comprometer la calidad del resultado.
        </p>
        
        {/* Nuevo Timeline para plazos de entrega */}
        <DeliveryTimelineContainer>
          <TimelineTrack>
            {timelineSteps.map((step) => (
              <TimelineStep key={step.number}>
                <TimelineDayBadge>
                  <TimelineDayNumber>Día</TimelineDayNumber>
                  <TimelineDayText>{step.number === 1 ? step.number : `${step.number}-${step.number+1}`}</TimelineDayText>
                </TimelineDayBadge>
                <TimelineStepCard>
                  <TimelineStepTitle>
                    {step.icon} {step.title.split(':')[1]}
                  </TimelineStepTitle>
                  <TimelineStepDescription>{step.description}</TimelineStepDescription>
                </TimelineStepCard>
              </TimelineStep>
            ))}
          </TimelineTrack>
        </DeliveryTimelineContainer>
        
        <p style={{ fontSize: '0.9rem', fontStyle: 'italic', margin: '2rem 0', textAlign: 'center' }}>
          <strong>Nota:</strong> Los plazos pueden variar según la complejidad del proyecto y la rapidez 
          con la que recibamos tus comentarios y aprobaciones. Para proyectos más complejos como webs corporativas avanzadas, 
          los plazos pueden extenderse a 2-3 semanas.
        </p>
      </Section>
      
      <Section>
        <CTASection>
          <CTATitle>¿Listo para empezar tu proyecto web?</CTATitle>
          <CTADescription>
            Ponte en contacto con nosotros hoy mismo y en aproximadamente una semana tendrás tu web profesional 
            lista para captar clientes.
          </CTADescription>
          <Button 
            as="a" 
            href="/contacto" 
            size="large"
          >
            Solicitar presupuesto
          </Button>
        </CTASection>
      </Section>
    </Layout>
  );
};

export default ProcesoPage; 